import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface League {
  id: number;
  name: string;
  description: string;
  venue: string;
  city: string;
  country: string;
  teamFormat: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  packages: Package[];
  squads: Squad[];
  sideEvents: SideEvent[];
  rules?: string;
}

interface Package {
  id: number;
  name: string;
  description: string;
  price: number;
  includes: number[];
}

interface Squad {
  id: number;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  gamesPerSquad: number;
}

interface SideEvent {
  id: number;
  name: string;
  description: string;
  type: string;
  cost: number;
}

interface Team {
  id: number;
  name: string;
  memberCount: number;
  members: string[];
}

const LeagueDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canAccessPremiumFeatures } = useSubscription();
  
  const [league, setLeague] = useState<League | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showTeamRegistrationModal, setShowTeamRegistrationModal] = useState(false);
  const [showNoTeamsModal, setShowNoTeamsModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [playerTeams, setPlayerTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [registrationError, setRegistrationError] = useState<string>('');
  const [registrationSuccess, setRegistrationSuccess] = useState<{
    teamName: string;
    leagueName: string;
    memberCount: number;
  } | null>(null);

  const fetchLeagueDetails = useCallback(async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/leagues/${id}/details`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Deduplicate side events by name, type, and cost (not by ID, as each squad creates a separate instance)
        if (data.sideEvents && data.sideEvents.length > 0) {
          const uniqueSideEvents = Array.from(
            new Map(
              data.sideEvents.map((event: SideEvent) => [
                `${event.name}-${event.type}-${event.cost}`, 
                event
              ])
            ).values()
          );
          data.sideEvents = uniqueSideEvents;
        }
        
        setLeague(data);
      }
    } catch (error) {
      console.error('Failed to fetch league details:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!canAccessPremiumFeatures) {
      setShowSubscriptionModal(true);
      return;
    }
    
    fetchLeagueDetails();
  }, [canAccessPremiumFeatures, fetchLeagueDetails]);

  const fetchPlayerTeams = async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/player/teams/eligible`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const teams = await response.json();
        setPlayerTeams(teams);
        
        if (teams.length === 0) {
          setShowNoTeamsModal(true);
        } else {
          setShowTeamRegistrationModal(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch player teams:', error);
    }
  };

  const validateTeamFormat = (team: Team): boolean => {
    if (!league) return false;
    
    const formatRequirements = {
      'individual': 1,
      'duos': 2,
      'trios': 3,
      'squads': 4,
      'fives': 5
    };

    const requiredMembers = formatRequirements[league.teamFormat as keyof typeof formatRequirements];
    return team.memberCount === requiredMembers;
  };

  const handleTeamRegistration = async (team: Team) => {
    if (!validateTeamFormat(team)) {
      setRegistrationError(`This league requires ${league?.teamFormat} format. Your team has ${team.memberCount} members.`);
      return;
    }

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/leagues/${id}/register-team`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ teamId: team.id })
      });

      if (response.ok) {
        const data = await response.json();
        setShowTeamRegistrationModal(false);
        
        // Set success data and show success modal
        setRegistrationSuccess({
          teamName: data.teamName || team.name,
          leagueName: league?.name || 'League',
          memberCount: data.membersRegistered || team.memberCount
        });
        setShowSuccessModal(true);
      } else {
        const errorData = await response.json();
        setRegistrationError(errorData.error || 'Failed to register team');
      }
    } catch (error) {
      console.error('Failed to register team:', error);
      setRegistrationError('An error occurred while registering the team');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (!user) {
    navigate('/player/dashboard');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-white">{league?.name || t('player.leagueDetails')}</h1>
              <p className="text-blue-200 mt-1">
                {league?.venue}, {league?.city}
              </p>
            </div>
            <button
              onClick={() => navigate('/player/leagues/browse')}
              className="px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition duration-200"
            >
              {t('player.backToBrowse')}
            </button>
          </div>
        </div>
      </div>

       {/* Help Card */}
      
        <div
          className="w-16 h-16 bg-green-600/30 rounded-full flex items-center justify-center shadow-lg cursor-pointer fixed bottom-8 right-8 z-50"
          onClick={() => setShowHelpModal(true)}
        >
          <span className="text-5xl text-green-300 font-bold">?</span>
        </div>
      

      {/* Help Modal */}
      {showHelpModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={e => {
            if (e.target === e.currentTarget) setShowHelpModal(false);
          }}
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-lg w-full mx-4 p-8 relative"
           style={{
              maxHeight: '80vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#6366f1 #18181b'
            }}>
            {/* Cool ? Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-600/30 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-5xl text-blue-300 font-bold">?</span>
              </div>
            </div>
            {/* Instructions Body */}
            <h2 className="text-2xl font-bold text-white text-center mb-4">{t('player.myTeamsRegisterHelpTitle')}</h2>
            <div className="text-blue-200 text-base whitespace-pre-line">
              {t('player.myTeamsRegisterHelpBody')}
            </div>
            <button
              onClick={() => setShowHelpModal(false)}
              className="mt-8 w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">⏳</div>
            <p className="text-blue-200 text-lg">{t('player.loadingLeagueDetails')}</p>
          </div>
        ) : league ? (
          <div className="grid gap-8">
            {/* League Overview */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">{t('player.leagueInformation')}</h2>
                  <div className="space-y-3 text-blue-200">
                    <div className="flex justify-between">
                      <span>{t('league.format')}:</span>
                      <span className="text-white capitalize font-medium">{league.teamFormat}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('league.startDate')}:</span>
                      <span className="text-white">{formatDate(league.startDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('league.endDate')}:</span>
                      <span className="text-white">{formatDate(league.endDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('league.venue')}:</span>
                      <span className="text-white">{league.venue}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('league.location')}:</span>
                      <span className="text-white">{league.city}, {league.country}</span>
                    </div>
                  </div>
                </div>
                
                <div className="text-center">
                  <button
                    onClick={fetchPlayerTeams}
                    className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-4 px-8 rounded-lg transition duration-200 transform hover:scale-105 shadow-lg"
                  >
                    🎳 {t('player.registerTeam')}
                  </button>
                  <p className="text-blue-300 text-sm mt-2">
                    {t('player.joinThisLeague')}
                  </p>
                </div>
              </div>
              
              {league.description && (
                <div className="mt-6 pt-6 border-t border-white/20">
                  <h3 className="text-xl font-bold text-white mb-3">{t('league.description')}</h3>
                  <p className="text-blue-200">{league.description}</p>
                </div>
              )}
            </div>

            {/* Packages */}
            {league.packages && league.packages.length > 0 && (
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h3 className="text-xl font-bold text-white mb-4">{t('player.availablePackages')}</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {league.packages.map((pkg) => (
                    <div key={pkg.id} className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-bold text-white">{pkg.name}</h4>
                        <span className="text-green-400 font-bold">${pkg.price}</span>
                      </div>
                      <p className="text-blue-200 text-sm mb-2">{pkg.description}</p>
                      {pkg.includes && pkg.includes.length > 0 && (
                        <p className="text-blue-300 text-xs">
                          {t('player.includesSquads', { count: pkg.includes.length })}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Schedule */}
            {league.squads && league.squads.length > 0 && (
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h3 className="text-xl font-bold text-white mb-4">{t('league.schedule')}</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {league.squads.map((squad) => (
                    <div key={squad.id} className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <h4 className="font-bold text-white mb-2">{squad.name}</h4>
                      <div className="space-y-1 text-sm text-blue-200">
                        <p><span className="text-white">{t('league.date')}:</span> {formatDate(squad.date)}</p>
                        <p><span className="text-white">{t('league.time')}:</span> {formatTime(squad.startTime)} - {formatTime(squad.endTime)}</p>
                        <p><span className="text-white">{t('league.games')}:</span> {squad.gamesPerSquad}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Side Events */}
            {league.sideEvents && league.sideEvents.length > 0 && (
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h3 className="text-xl font-bold text-white mb-4">{t('league.sideEvents')}</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {league.sideEvents.map((event) => (
                    <div key={event.id} className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-bold text-white">{event.name}</h4>
                        <span className="text-yellow-400 font-bold">${event.cost}</span>
                      </div>
                      <p className="text-blue-200 text-sm mb-1">{event.description}</p>
                      <p className="text-blue-300 text-xs capitalize">{t('league.type')}: {event.type.replace('_', ' ')}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">❌</div>
            <h3 className="text-xl font-bold text-white mb-2">{t('player.leagueNotFound')}</h3>
            <p className="text-blue-200">{t('player.leagueNotFoundMessage')}</p>
          </div>
        )}
      </div>

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-900 to-purple-800 rounded-xl p-8 border border-purple-400 max-w-md w-full">
            <div className="text-center">
              <div className="text-6xl mb-4">👑</div>
              <h3 className="text-2xl font-bold text-white mb-4">{t('player.upgradeToPremium')}</h3>
              <p className="text-purple-200 mb-6">
                {t('player.subscriptionMessage')}
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    // TODO: Implement subscription logic
                    console.log('Subscribe clicked');
                  }}
                  className="flex-1 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-yellow-900 font-bold py-3 px-6 rounded-lg transition duration-200"
                >
                  {t('player.subscribeNow')}
                </button>
                <button
                  onClick={() => {
                    setShowSubscriptionModal(false);
                    navigate('/player/leagues/browse');
                  }}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Teams Modal */}
      {showNoTeamsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-xl p-8 border border-blue-400 max-w-md w-full">
            <div className="text-center">
              <div className="text-6xl mb-4">👥</div>
              <h3 className="text-2xl font-bold text-white mb-4">{t('player.noEligibleTeams')}</h3>
              <div className="text-blue-200 mb-6 text-left space-y-3">
                <p>{t('player.noTeamsMessage')}</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>{t('player.noTeamsReason1')}</li>
                  <li>{t('player.noTeamsReason2')}</li>
                  <li>{t('player.noTeamsReason3')}</li>
                </ul>
                <p className="text-sm font-medium text-yellow-300">
                  💡 {t('player.checkMyTeamsHint')}
                </p>
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    setShowNoTeamsModal(false);
                    navigate('/player/teams');
                  }}
                  className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
                >
                  {t('player.goToMyTeams')}
                </button>
                <button
                  onClick={() => setShowNoTeamsModal(false)}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Registration Success Modal */}
      {showSuccessModal && registrationSuccess && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-green-900 to-green-800 rounded-xl p-8 border border-green-400 max-w-lg w-full">
            <div className="text-center">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-2xl font-bold text-white mb-4">{t('player.registrationSuccessful')}</h3>
              <div className="text-green-200 mb-6 space-y-3">
                <div className="bg-white/10 rounded-lg p-4 text-left">
                  <h4 className="font-semibold text-white mb-2">{t('player.registrationDetails')}:</h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-green-300">{t('league.team')}:</span> {registrationSuccess.teamName}</p>
                    <p><span className="text-green-300">{t('league.league')}:</span> {registrationSuccess.leagueName}</p>
                    <p><span className="text-green-300">{t('player.membersRegistered')}:</span> {registrationSuccess.memberCount}</p>
                  </div>
                </div>
                <p className="text-sm text-center">
                  {t('player.registrationSuccessMessage')}
                </p>
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setRegistrationSuccess(null);
                    navigate('/player/teams');
                  }}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
                >
                  {t('player.viewMyTeams')}
                </button>
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setRegistrationSuccess(null);
                    navigate('/player/leagues/browse');
                  }}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
                >
                  {t('player.browseMoreLeagues')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Team Registration Modal */}
      {showTeamRegistrationModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-xl p-8 border border-blue-400 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="text-4xl mb-4">🎳</div>
              <h3 className="text-2xl font-bold text-white mb-2">{t('player.selectTeamToRegister')}</h3>
              <p className="text-blue-200 text-sm">{t('player.chooseTeamMessage')}</p>
            </div>

            {registrationError && (
              <div className="bg-red-500/20 border border-red-400 rounded-lg p-3 mb-4">
                <p className="text-red-200 text-sm">{registrationError}</p>
              </div>
            )}

            <div className="space-y-3 mb-6">
              {playerTeams.map((team) => (
                <div
                  key={team.id}
                  className={`p-4 rounded-lg border cursor-pointer transition duration-200 ${
                    validateTeamFormat(team)
                      ? 'bg-white/10 border-white/20 hover:bg-white/20'
                      : 'bg-red-500/10 border-red-400 opacity-60'
                  }`}
                  onClick={() => {
                    if (validateTeamFormat(team)) {
                      setSelectedTeam(team);
                      setRegistrationError('');
                    } else {
                      setRegistrationError(t('player.teamFormatMismatch', { format: league?.teamFormat }));
                    }
                  }}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-bold text-white">{team.name}</h4>
                      <p className="text-blue-200 text-sm">{t('player.teamMembers', { count: team.memberCount })}</p>
                    </div>
                    {selectedTeam?.id === team.id && (
                      <div className="text-green-400">✓</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mb-4">
              <p className="text-blue-300 text-sm">{t('player.notSeeingTeam')}</p>
              <button
                onClick={() => {
                  setShowTeamRegistrationModal(false);
                  navigate('/player/teams');
                }}
                className="text-blue-400 hover:text-blue-300 text-sm underline mt-1"
              >
                {t('player.browseTeams')}
              </button>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => {
                  if (selectedTeam) {
                    handleTeamRegistration(selectedTeam);
                  }
                }}
                disabled={!selectedTeam}
                className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-lg transition duration-200"
              >
                {t('player.registerTeam')}
              </button>
              <button
                onClick={() => {
                  setShowTeamRegistrationModal(false);
                  setSelectedTeam(null);
                  setRegistrationError('');
                }}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeagueDetail;